import React, { useState } from 'react';
import { Image } from 'semantic-ui-react';
import { getFullImagePathByType, uploadTypes } from '../../constants/imageUpload';

export type ImageShape = 'circle' | 'square' | 'rounded';

interface FlexibleImageProps {
  /** Primary image URL */
  imageUrl?: string;
  /** Fallback image URLs in order of preference */
  fallbackImageUrls?: string[];
  /** Alternative text for the image */
  alt?: string;
  /** Size of the image - maps to Semantic UI size classes */
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  /** Shape of the image */
  shape?: ImageShape;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show a placeholder when no image is available */
  showPlaceholder?: boolean;
  /** Custom placeholder content */
  placeholder?: React.ReactNode;
}

const getDefaultPlaceholder = (shape: ImageShape) => (
  <div style={{
    width: '100%',
    height: '100%',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6c757d',
    fontSize: '14px',
    borderRadius: shape === 'circle' ? '50%' : shape === 'rounded' ? '8px' : '0'
  }}>
    No Image
  </div>
);

export const FlexibleImage: React.FC<FlexibleImageProps> = ({
  imageUrl,
  fallbackImageUrls = [],
  alt = '',
  size = 'medium',
  shape = 'rounded',
  className = '',
  showPlaceholder = true,
  placeholder
}) => {
  // Find the first available image URL
  const getImageUrlToDisplay = (): string => {
    const allUrls = [imageUrl, ...fallbackImageUrls].filter(Boolean) as string[];

    for (const url of allUrls) {
      if (!url) {
        continue;
      }
      return `${url}?w=200`;
    }

    return '';
  };

  const displayUrl = getImageUrlToDisplay();

  // Get the shape-specific CSS classes
  const getShapeClasses = (): string => {
    switch (shape) {
      case 'circle':
        return 'circular';
      case 'rounded':
        return 'rounded';
      case 'square':
      default:
        return '';
    }
  };

  const shapeClasses = getShapeClasses();
  const finalPlaceholder = placeholder || getDefaultPlaceholder(shape);

  // If no image URL is available, show placeholder
  if (!displayUrl) {
    return showPlaceholder ? (
      <div className={`ui ${size} ${shapeClasses} image ${className}`}>
        {finalPlaceholder}
      </div>
    ) : null;
  }

  return (
    <Image
      src={displayUrl}
      alt={alt}
      size={size}
      circular={shape === 'circle'}
      rounded={shape === 'rounded'}
      className={className}
    />
  );
};

// Export CircularImage as an alias for backward compatibility
export const CircularImage: React.FC<Omit<FlexibleImageProps, 'shape'>> = (props) => (
  <FlexibleImage {...props} shape="rounded" />
);

// Default export for easier importing
export default FlexibleImage;
